import { useState } from 'react'
import { Alert, App, Button, Card, Col, Form, Input, Row, Space, Switch, Tag, Tooltip, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { PageHeader } from '@/shared/ui/PageHeader'
import {
  WHATSAPP_RULES,
  fetchWhatsAppSettings,
  resetWhatsAppSettings,
  saveWhatsAppSettings,
  sendWhatsAppTest,
  type WhatsAppSettings,
  type WhatsAppTestResult,
} from '@/features/notify/whatsappApi'
import { PHONE_E164, normalizePhone } from '@/features/subscriber/subscriberApi'

interface FormValues {
  enabled: boolean
  businessPhone?: string
  phoneNumberId?: string
  accessToken?: string
  apiVersion?: string
}

export function WhatsAppSettingsPage() {
  const { t } = useTranslation()
  const settings = useQuery({ queryKey: queryKeys.notify.whatsappSettings, queryFn: fetchWhatsAppSettings })

  return (
    <>
      <PageHeader title={t('nav.whatsapp')} description={t('pageIntro.whatsapp')} />
      {settings.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(settings.error, t)} />
      ) : null}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={t('whatsapp.connectionTitle')} loading={settings.isPending}>
            {settings.data ? (
              <ConnectionForm key={`${settings.data.source}-${settings.data.updatedAt ?? ''}`} settings={settings.data} />
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <TestMessageCard />
        </Col>
      </Row>
    </>
  )
}

function ConnectionForm({ settings }: { settings: WhatsAppSettings }) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const storedToken = settings.source === 'DATABASE' && settings.accessTokenSet
  const [replacingToken, setReplacingToken] = useState(!storedToken)

  const onSaved = (next: WhatsAppSettings) => queryClient.setQueryData(queryKeys.notify.whatsappSettings, next)
  const save = useMutation({
    mutationFn: saveWhatsAppSettings,
    onSuccess: (next) => {
      onSaved(next)
      void message.success(t('whatsapp.saved'))
    },
  })
  const reset = useMutation({
    mutationFn: resetWhatsAppSettings,
    onSuccess: (next) => {
      onSaved(next)
      void message.success(t('whatsapp.resetDone'))
    },
  })
  const busy = save.isPending || reset.isPending
  const failed = save.error ?? reset.error

  const confirmReset = () =>
    modal.confirm({
      title: t('whatsapp.resetConfirmTitle'),
      content: t('whatsapp.resetConfirmText'),
      okButtonProps: { danger: true },
      okText: t('whatsapp.reset'),
      cancelText: t('common.cancel'),
      onOk: () => reset.mutateAsync(),
    })

  const submit = (values: FormValues) =>
    save.mutate({
      enabled: values.enabled,
      businessPhone: normalizePhone(values.businessPhone),
      phoneNumberId: values.phoneNumberId?.trim() || null,
      accessToken: replacingToken ? values.accessToken?.trim() || null : null,
      apiVersion: values.apiVersion?.trim() || null,
    })

  const requiredWhenEnabled = (filled: (value: string | undefined) => boolean, key: string) => ({
    validator: (_: unknown, value: string | undefined) =>
      !form.getFieldValue('enabled') || filled(value) ? Promise.resolve() : Promise.reject(new Error(t(key))),
  })

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      disabled={busy}
      initialValues={{
        enabled: settings.enabled,
        businessPhone: settings.businessPhone ?? undefined,
        phoneNumberId: settings.phoneNumberId ?? undefined,
        apiVersion: settings.apiVersion ?? undefined,
      }}
    >
      {settings.source === 'CONFIG' ? (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('whatsapp.fromConfig')} />
      ) : null}
      {failed ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(failed, t)} /> : null}

      <Form.Item name="enabled" label={t('whatsapp.enabled')} valuePropName="checked" extra={t('whatsapp.enabledHint')}>
        <Switch />
      </Form.Item>

      <Form.Item
        name="businessPhone"
        label={t('whatsapp.businessPhone')}
        extra={t('whatsapp.businessPhoneHint')}
        rules={[
          {
            validator: (_, value: string | undefined) => {
              const clean = normalizePhone(value)
              if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
              return Promise.reject(new Error(t('whatsapp.phoneFormat')))
            },
          },
        ]}
      >
        <Input dir="ltr" inputMode="tel" autoComplete="off" />
      </Form.Item>

      <Form.Item
        name="phoneNumberId"
        label={t('whatsapp.phoneNumberId')}
        extra={t('whatsapp.phoneNumberIdHint')}
        dependencies={['enabled']}
        rules={[
          { pattern: WHATSAPP_RULES.phoneNumberId, message: t('whatsapp.phoneNumberIdFormat') },
          requiredWhenEnabled((value) => Boolean(value?.trim()), 'whatsapp.phoneNumberIdRequired'),
        ]}
      >
        <Input dir="ltr" inputMode="numeric" autoComplete="off" />
      </Form.Item>

      {replacingToken ? (
        <Form.Item
          name="accessToken"
          label={t('whatsapp.accessToken')}
          extra={t('whatsapp.accessTokenHint')}
          dependencies={['enabled']}
          rules={[
            { pattern: WHATSAPP_RULES.accessToken, message: t('whatsapp.accessTokenFormat') },
            requiredWhenEnabled((value) => storedToken || Boolean(value?.trim()), 'whatsapp.accessTokenRequired'),
          ]}
        >
          <Input.Password dir="ltr" autoComplete="new-password" />
        </Form.Item>
      ) : (
        <Form.Item label={t('whatsapp.accessToken')} extra={t('whatsapp.accessTokenStoredHint')}>
          <Space wrap>
            <Tag color="green">{t('whatsapp.accessTokenSet')}</Tag>
            <Button size="small" onClick={() => setReplacingToken(true)}>
              {t('whatsapp.replaceToken')}
            </Button>
          </Space>
        </Form.Item>
      )}

      <Form.Item
        name="apiVersion"
        label={t('whatsapp.apiVersion')}
        extra={t('whatsapp.apiVersionHint', { version: settings.defaultApiVersion ?? '—' })}
        rules={[{ pattern: WHATSAPP_RULES.apiVersion, message: t('whatsapp.apiVersionFormat') }]}
      >
        <Input dir="ltr" autoComplete="off" placeholder={settings.defaultApiVersion ?? undefined} style={{ maxWidth: 160 }} />
      </Form.Item>

      <Space wrap>
        <Button type="primary" loading={save.isPending} onClick={() => submitOnValid(form, submit)}>
          {t('common.save')}
        </Button>
        {settings.source === 'DATABASE' ? (
          <Button danger loading={reset.isPending} onClick={confirmReset}>
            {t('whatsapp.reset')}
          </Button>
        ) : null}
        {settings.updatedAt ? (
          <Tooltip title={utcTooltip(settings.updatedAt)}>
            <Typography.Text type="secondary">
              {t('whatsapp.updatedAt', { at: formatDateTime(settings.updatedAt) })}
            </Typography.Text>
          </Tooltip>
        ) : null}
      </Space>
    </Form>
  )
}

function TestMessageCard() {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ to?: string }>()
  const send = useMutation({ mutationFn: (to: string) => sendWhatsAppTest(to) })

  return (
    <Card title={t('whatsapp.testTitle')}>
      <Typography.Paragraph type="secondary">{t('whatsapp.testHint')}</Typography.Paragraph>
      <Form form={form} layout="vertical" disabled={send.isPending}>
        <Form.Item
          name="to"
          label={t('whatsapp.testRecipient')}
          rules={[
            {
              validator: (_, value: string | undefined) => {
                const clean = normalizePhone(value)
                if (clean !== null && PHONE_E164.test(clean)) return Promise.resolve()
                return Promise.reject(new Error(t('whatsapp.phoneFormat')))
              },
            },
          ]}
        >
          <Input dir="ltr" inputMode="tel" autoComplete="off" />
        </Form.Item>
        <Button
          type="primary"
          loading={send.isPending}
          onClick={() => submitOnValid(form, (values) => send.mutate(normalizePhone(values.to) ?? ''))}
        >
          {t('whatsapp.testSend')}
        </Button>
      </Form>
      {send.error ? (
        <Alert type="error" showIcon style={{ marginTop: 16 }} message={translateError(send.error, t)} />
      ) : null}
      {send.data ? <TestResult result={send.data} /> : null}
    </Card>
  )
}

function TestResult({ result }: { result: WhatsAppTestResult }) {
  const { t } = useTranslation()
  const sent = result.status === 'SENT'
  // Failure detail is server text in Uzbek; other languages rely on the translated status alone.
  const uzbek = t('common.langCode', { defaultValue: '' }) === 'uz'
  const detail = sent ? result.providerMessageId : uzbek ? result.detail : null
  return (
    <Alert
      style={{ marginTop: 16 }}
      showIcon
      type={sent ? 'success' : result.status === 'TEMPORARY_FAILURE' ? 'warning' : 'error'}
      message={t(`whatsapp.testStatus.${result.status}`)}
      description={
        detail ? (
          <Typography.Text dir="ltr" copyable>
            {detail}
          </Typography.Text>
        ) : undefined
      }
    />
  )
}
